import React, { useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Layout } from "../Layouts/Layout";
import { Campaign, CampaignState, CampaignType } from "../types";
import { CampaignCard } from "../Components/Campaign/Card";
import { Add } from "@mui/icons-material";
import { useNavigate } from "react-router";
import {
  GET_SECTIONS,
  SectionsResult,
  GET_FORMS,
  FormsResult,
  GET_CAMPAIGNS,
  CampaignsResult,
  ADD_CAMPAIGN,
  UPDATE_CAMPAIGN,
  DELETE_CAMPAIGN,
  SEND_CAMPAIGN_REMINDERS,
} from "../Queries";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import { sortByActivity } from "../Data";
import { useErrorBoundary } from "../Contexts/ErrorContext";

export const Assignments = () => {
  const navigate = useNavigate();
  const [t] = useTranslation();
  const { setError } = useErrorBoundary();

  //
  // State
  //

  const [sectionId, setSectionId] = useState(undefined as string | undefined);
  const [addBusy, setAddBusy] = useState(false);

  //
  // Queries
  //
  const { loading: sectionsLoading, data: sectionsData } =
    useQuery<SectionsResult>(GET_SECTIONS, {
      variables: { filters: {} },
      onError: (error) => setError(t("errors.sections.list"), error),
    });

  useQuery<FormsResult>(GET_FORMS, {
    variables: { filters: {} },
    onError: (error) => setError(t("errors.forms.list"), error),
  });

  const { loading: campaignsLoading, data: campaignsData } =
    useQuery<CampaignsResult>(GET_CAMPAIGNS, {
      variables: {
        filters: {
          sectionId: sectionId ? sectionId : undefined,
          campaignType: { exact: CampaignType.Assignment },
        },
      },
      onError: (error) => setError(t("errors.campaigns.list"), error),
    });

  //
  // Mutations
  //

  const [addCampaign] = useMutation(ADD_CAMPAIGN, {
    refetchQueries: ["GetCampaigns"],
    onError: (error) => setError(t("errors.campaigns.create"), error),
  });

  const [updateCampaign] = useMutation(UPDATE_CAMPAIGN, {
    refetchQueries: ["GetCampaigns"],
    onError: (error) => setError(t("errors.campaigns.update"), error),
  });

  const [deleteCampaign] = useMutation(DELETE_CAMPAIGN, {
    refetchQueries: ["GetCampaigns"],
    onError: (error) => setError(t("errors.campaigns.delete"), error),
  });

  const [sendCampaignReminders] = useMutation(SEND_CAMPAIGN_REMINDERS, {
    refetchQueries: ["GetCampaign"],
    onError: (error) => setError(t("errors.campaigns.sendReminder"), error),
  });

  const sections = sectionsData ? sectionsData.sections : undefined;
  const campaigns = campaignsData ? campaignsData.campaigns : [];
  const showLoading = sectionsLoading || sectionsLoading || campaignsLoading;

  if (!sectionsLoading && !sections) {
    throw new Error("No sections found");
  }

  //
  // Event handlers
  //

  async function handleAddCampaign() {
    const now = new Date();
    const dueDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      0,
      0,
    );

    setAddBusy(true);
    const { data: newCampaignResult } = await addCampaign({
      variables: {
        name: "New Assignment",
        sectionId: sectionId,
        dueDate,
        campaignType: CampaignType.Assignment,
      },
    });
    setAddBusy(false);

    if (!newCampaignResult?.addCampaign) {
      return;
    }
    const newCampaign = newCampaignResult.addCampaign;
    navigate(`/campaigns/${newCampaign.id}`);
  }

  async function handleCampaignChange(
    changedCampaign: Campaign,
    activityLabel: string,
  ) {
    await updateCampaign({
      variables: {
        ...changedCampaign,
        activityLabel,
      },
    });
  }

  async function handleDelete(campaign: Campaign) {
    const confirmed = window.confirm(
      t("assignments.confirmDelete", { name: campaign.name }) as string,
    );
    if (!confirmed) {
      return;
    }

    await deleteCampaign({
      variables: {
        id: campaign.id,
      },
    });
  }

  async function handleRemind(campaign: Campaign) {
    const sent = await sendCampaignReminders({
      variables: {
        id: campaign.id,
      },
      context: {
        debounceKey: `campaign-${campaign.id}`,
      },
    });

    if (sent) {
      alert("Reminders sent successfully");
    } else {
      alert("No reminders to send");
    }
  }

  //
  // Components
  //

  const CampaignGroup = ({
    label,
    states,
  }: {
    label: string;
    states: CampaignState[];
  }) => {
    const filteredCampaigns = sortByActivity(
      campaigns,
      t("assignments.events.send"),
      states,
    );

    if (!filteredCampaigns || filteredCampaigns.length === 0) {
      return null;
    }

    return (
      <Box
        sx={{
          mb: 6,
        }}
      >
        <Typography
          variant="h5"
          color="primary"
          sx={{ fontWeight: "bold", mb: 2 }}
        >
          {label}
        </Typography>

        <Grid container spacing={2}>
          {filteredCampaigns.map((campaign) => (
            <Grid item key={campaign.id} xs={12} md={6} lg={4}>
              <CampaignCard
                campaign={campaign}
                onChange={handleCampaignChange}
                onDelete={handleDelete}
                onRemind={handleRemind}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  return (
    <Layout
      breadcrumbs={[{ title: t("navigation.surveys"), link: "/" }]}
      loading={showLoading}
    >
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <LoadingButton
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddCampaign}
            loading={addBusy}
          >
            {t("assignments.add")}
          </LoadingButton>
        </Box>

        <CampaignGroup
          label={t("assignments.groups.active")}
          states={[CampaignState.Active, CampaignState.AssignmentActive]}
        />
        <CampaignGroup
          label={t("assignments.groups.scheduled")}
          states={[CampaignState.Scheduled, CampaignState.AssignmentScheduled]}
        />
        <CampaignGroup
          label={t("assignments.groups.draft")}
          states={[CampaignState.Draft]}
        />
        <CampaignGroup
          label={t("assignments.groups.closed")}
          states={[CampaignState.Closed, CampaignState.AssignmentClosed]}
        />
      </Box>
    </Layout>
  );
};
